import { useEffect, useState } from 'react';
import { Target, CalendarClock, Info } from 'lucide-react';
import { supabase } from '../services/supabase';

// Panel de tecnificación individual — SOLO VISUALIZACIÓN (motor común).
//
// Tras la unificación en el motor común (mig 151/152, PRs #275/#276/#279+) esta
// página deja de crear/editar nada: la creación de sesiones se hace en Agenda
// (Sesiones → Programar / gestión de propuestas) por el motor común
// (generate_sessions aforo 1 + trigger J4). Aquí solo se consulta:
//   - Activas    → user_subscriptions class_type='individual' status='activated'.
//   - Solicitudes → propuestas pendientes: class_type='individual'
//                   status='purchased' con proposed_at (mig 152). Se gestionan en
//                   Agenda (la tarea del dashboard enlaza a /agenda?proposals=1).
//
// NO contiene acciones de mutación (admin_create_individual_series, publicar/
// retirar huecos, reembolso): se retiraron al pasar a solo-lectura.
//
// ⚠️ HUBO UNA TERCERA PESTAÑA, "Huecos", que listaba individual_tecnif_slots. La
// retira la mig 189 con el resto del modelo de huecos. Se llegó a creer que servía
// para "proponer horario cuando no hay disponible", y NO: ese flujo existe, pero va
// por propose_individual_tecnif → proposed_* en user_subscriptions → la pestaña
// "Solicitudes" de aquí, TareasAdminPanel y SesionesPage. La de "Huecos" era del
// modelo INVERSO (el admin publicaba, el alumno elegía). Desde el commit 542c261
// (3-jul-2026), que borró el formulario de publicar, NINGÚN camino de la aplicación
// puede insertar una fila en esa tabla —no existe un solo INSERT en el proyecto—,
// así que la pestaña mostraba un catálogo que nadie podía llenar.

// Suscripción individual activa (motor común).
interface ActiveSub {
  id: string;
  user_id: string;
  day_of_week: number | null;
  time_start: string | null;
  period_month: string | null;
  student: { full_name: string } | null;
  group: { name: string; monitor: { full_name: string } | null } | null;
}

// Propuesta de horario pendiente (motor común, mig 152).
interface ProposalSub {
  id: string;
  user_id: string;
  proposed_day_of_week: number | null;
  proposed_time_start: string | null;
  proposed_at: string | null;
  student: { full_name: string } | null;
}

const DAY_LABELS: Record<number, string> = {
  0: 'Domingo',
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
};

const MONTHS_ES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

function dayLabel(dow: number | null): string {
  return dow === null ? '—' : (DAY_LABELS[dow] ?? '—');
}

function hhmm(t: string | null): string {
  return t ? t.slice(0, 5) : '—';
}

function monthLabel(periodMonth: string | null): string {
  if (!periodMonth) return '—';
  const [y, m] = periodMonth.split('-').map(Number);
  return `${MONTHS_ES[m - 1]} ${y}`;
}

export default function TecnificacionIndividualPage() {
  const [activeSubs, setActiveSubs] = useState<ActiveSub[]>([]);
  const [proposals, setProposals] = useState<ProposalSub[]>([]);
  const [tab, setTab] = useState<'active' | 'requested'>('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [{ data: actives, error: e1 }, { data: props, error: e2 }] = await Promise.all([
        // Activas — motor común (user_subscriptions).
        supabase
          .from('user_subscriptions')
          .select(
            'id, user_id, day_of_week, time_start, period_month, ' +
              'student:profiles!user_id(full_name), ' +
              'group:groups!group_id(name, monitor:profiles!monitor_id(full_name))'
          )
          .eq('class_type', 'individual')
          .eq('status', 'activated')
          .order('activated_at', { ascending: false }),
        // Solicitudes — propuestas pendientes (motor común, mig 152).
        supabase
          .from('user_subscriptions')
          .select(
            'id, user_id, proposed_day_of_week, proposed_time_start, proposed_at, ' +
              'student:profiles!user_id(full_name)'
          )
          .eq('class_type', 'individual')
          .eq('status', 'purchased')
          .not('proposed_at', 'is', null)
          .order('proposed_at', { ascending: true }),
      ]);
      if (e1 || e2) {
        setError('No se pudieron cargar los datos.');
      }
      setActiveSubs((actives ?? []) as unknown as ActiveSub[]);
      setProposals((props ?? []) as unknown as ProposalSub[]);
    } catch {
      setError('No se pudieron cargar los datos.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Target size={20} className="text-gold-primary" />
        <h1 className="font-oswald text-xl text-white">Tecnificación individual</h1>
      </div>

      <p
        className="flex items-start gap-2 text-text-secondary text-sm bg-bg-elevated rounded-lg p-3"
        data-testid="tecnif-readonly-note"
      >
        <Info size={16} className="text-gold-primary flex-shrink-0 mt-0.5" />
        <span>
          Panel de consulta. La creación de sesiones y la gestión de propuestas se hacen en{' '}
          <span className="text-white">Agenda</span> por el motor común (aforo 1). Aquí solo se
          visualiza el estado.
        </span>
      </p>

      {error && (
        <p className="text-error text-sm bg-error/10 rounded-lg p-3" data-testid="tecnif-error">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <button
          className={tab === 'active' ? 'btn-gold text-xs' : 'btn-ghost text-xs'}
          onClick={() => setTab('active')}
          data-testid="tecnif-tab-active"
        >
          Activas ({activeSubs.length})
        </button>
        <button
          className={tab === 'requested' ? 'btn-gold text-xs' : 'btn-ghost text-xs'}
          onClick={() => setTab('requested')}
          data-testid="tecnif-tab-requested"
        >
          Solicitudes ({proposals.length})
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
        </div>
      ) : tab === 'active' ? (
        <div className="card">
          {activeSubs.length === 0 ? (
            <p className="text-text-secondary text-sm py-2" data-testid="tecnif-active-empty">
              No hay tecnificaciones individuales en curso.
            </p>
          ) : (
            <div className="space-y-2">
              {activeSubs.map((a) => (
                <div
                  key={a.id}
                  data-testid={`tecnif-active-row-${a.id}`}
                  className="flex items-center justify-between gap-3 rounded-lg border border-bg-border bg-bg-elevated p-3"
                >
                  <div>
                    <p className="text-white font-inter text-sm">{a.student?.full_name ?? '—'}</p>
                    <p className="text-text-secondary text-xs">
                      {dayLabel(a.day_of_week)} {hhmm(a.time_start)}
                      {a.group?.monitor?.full_name ? ` · ${a.group.monitor.full_name}` : ''}
                    </p>
                  </div>
                  <span className="text-text-secondary text-xs flex-shrink-0">
                    {monthLabel(a.period_month)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          {proposals.length === 0 ? (
            <p className="text-text-secondary text-sm py-2" data-testid="tecnif-requested-empty">
              No hay propuestas pendientes.
            </p>
          ) : (
            <div className="space-y-2">
              <p className="text-text-secondary text-xs flex items-center gap-2 mb-1">
                <CalendarClock size={14} className="text-gold-primary" />
                Resuélvelas en Agenda (crear la sesión + activar por el motor común).
              </p>
              {proposals.map((p) => (
                <div
                  key={p.id}
                  data-testid={`tecnif-requested-row-${p.id}`}
                  className="flex items-center justify-between gap-3 rounded-lg border border-bg-border bg-bg-elevated p-3"
                >
                  <div>
                    <p className="text-white font-inter text-sm">{p.student?.full_name ?? '—'}</p>
                    <p className="text-text-secondary text-xs">
                      Propone: {dayLabel(p.proposed_day_of_week)} {hhmm(p.proposed_time_start)}
                    </p>
                  </div>
                  <span className="text-gold-primary bg-gold-primary/10 text-xs px-2 py-1 rounded flex-shrink-0">
                    Pendiente
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
